import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useApp';

/**
 * The routes `RequireAuth` turns away, as prefixes rather than exact paths so `/poruke/:id` is
 * covered by `/poruke` and `/uredi-oglas/:shortCode` by `/uredi-oglas`.
 *
 * Kept beside the guard it mirrors: a route added to one and not the other simply falls back to the
 * old behaviour of bouncing, which is survivable — silently letting a signed-out visitor through
 * would not be.
 */
const GATED = ['/postavi-oglas', '/uredi-oglas', '/moji-oglasi', '/sacuvani', '/poruke', '/podesavanja'];

export interface GuardedLink {
  to: string;
  state?: unknown;
  replace: boolean;
  viewTransition: boolean;
  onClick?: () => void;
}

/**
 * Where a navigation control should actually point, decided before the tap rather than after it.
 *
 * Pointing a control at a gated route while nobody is signed in makes one tap into two
 * navigations: the router commits the gated URL, `RequireAuth` paints a frame with no page in it,
 * and an effect then redirects to the login page. On a phone that is visible and awful — the entry
 * animation plays twice, and because every one of these links carries `viewTransition`, the second
 * navigation starts a view transition while the browser is still running the first, so the old
 * snapshot is left frozen over the top and the screen jumps.
 *
 * Resolving the destination here collapses that back into the single navigation it always was, and
 * the intended destination still travels along in `state.from` so signing in lands them where they
 * meant to go.
 *
 * The two flags close the other half of it. Tabs collapse onto the same destination when signed
 * out — "Poruke", "Sačuvani" and the account tab all lead to the login page — so tapping around the
 * bar pushed a pile of identical history entries and the back button appeared to do nothing several
 * times in a row. A navigation that does not change where you are replaces instead of pushing, and
 * does not ask for a view transition it has nothing to transition between.
 *
 * What such a tap does instead is go back to the top, which is what a tab bar is expected to do when
 * you tap the tab you are already on. Until now that came for free from the pointless push — the
 * scroll restorer treats an arrival as an arrival — and it would have been quietly lost here.
 */
export function useGuardedLink(): (to: string) => GuardedLink {
  const { user, isLoading } = useAuth();
  const { pathname, search, state } = useLocation();

  const here = pathname + search;

  return useCallback(
    (to: string) => {
      // Compared on the path alone, so a destination that carries a query string is still matched.
      const path = to.split(/[?#]/)[0];

      /*
        `isLoading` is not the same as signed out, and treating it as such is how this fix would
        have broken the case it was meant to protect. On a cold start the only evidence of a session
        is an httpOnly cookie, so `user` is null for as long as the silent refresh takes — and the
        tab bar is on screen throughout. Diverting during that window would send a signed-in visitor
        to the login page and then bounce them off it the moment the refresh landed, which is the
        very double navigation this hook exists to remove. While it is genuinely unknown the link
        points where it says it does and `RequireAuth` holds a spinner until the answer arrives.
      */
      const gated =
        !isLoading && !user && GATED.some((route) => path === route || path.startsWith(`${route}/`));

      const destination = gated ? '/prijava' : to;
      const staying = destination === here;

      return {
        to: destination,
        /*
          Staying put must not wipe what is already there. `state.from` is what returns someone to
          the page they were reaching for once they have signed in, and tapping a second tab on the
          login page — or the login link itself — would otherwise clear it and land them on the home
          page after a successful sign-in.
        */
        state: gated ? { from: to } : staying ? state : undefined,
        replace: staying,
        viewTransition: !staying,
        // Left to the CSS, which is smooth on `html` and forced to `auto` under reduced motion.
        onClick: staying ? () => window.scrollTo({ top: 0 }) : undefined,
      };
    },
    [user, isLoading, here, state],
  );
}
